<template>
  <!-- Поля обёрнуты в <label> — связь с контролом есть и без id -->
  <!-- eslint-disable vuejs-accessibility/label-has-for -->
  <section class="w-panel" aria-label="Заполнение графика">
    <div class="w-panel-head">
      <h2>Заполнить график</h2>
      <button
        class="w-button w-icon w-ghost"
        type="button"
        aria-label="Закрыть заполнение графика"
        @click="store.builderOpen = false"
      >
        <q-icon name="close" />
      </button>
    </div>

    <div class="w-builder-fields">
      <label class="w-field">
        С даты
        <input v-model.lazy="plan.from" type="date" />
      </label>
      <label class="w-field">
        По дату
        <input v-model.lazy="plan.to" type="date" :disabled="plan.pattern === 'once'" />
      </label>
      <label class="w-field w-builder-repeat">
        Повторение
        <select v-model="plan.pattern">
          <option v-for="option in PATTERNS" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </label>
      <label v-if="plan.pattern === '2on2'" class="w-field">
        Начало цикла
        <input v-model.lazy="plan.anchor" type="date" />
      </label>
      <label class="w-field">
        Начало приёма
        <input v-model.lazy="plan.start" type="time" step="60" />
      </label>
      <label class="w-field">
        Окончание
        <input v-model.lazy="plan.end" type="time" step="60" />
      </label>
      <label class="w-field">
        Длительность, мин
        <input v-model.lazy.number="plan.duration" type="number" min="1" step="1" />
      </label>
      <label class="w-field">
        Кабинет
        <select v-model.number="plan.roomId">
          <option v-for="room in store.allowedRooms" :key="room.id" :value="room.id">
            {{ room.number ?? room.id }} · {{ room.purpose || room.name }}
          </option>
        </select>
      </label>
    </div>

    <div class="w-builder-breaks">
      <div class="w-breaks-head">
        <h3>Регламентированные перерывы</h3>
        <button class="w-button w-ghost" type="button" @click="store.addPlanBreak()">
          <q-icon name="add" />
          Перерыв
        </button>
      </div>
      <div class="w-break-list">
        <div v-for="(item, index) in plan.breaks" :key="index" class="w-break-row">
          <label class="w-field">
            Перерыв
            <input
              v-model.lazy="item.label"
              type="text"
              :aria-label="`Название перерыва ${index + 1}`"
            />
          </label>
          <label class="w-field">
            Закрывает
            <select v-model="item.scope" :aria-label="`Область перерыва ${index + 1}`">
              <option value="staff">Врача</option>
              <option value="room">Кабинет</option>
            </select>
          </label>
          <label class="w-field">
            С
            <input
              v-model.lazy="item.start"
              type="time"
              :aria-label="`Начало перерыва ${index + 1}`"
            />
          </label>
          <label class="w-field">
            До
            <input
              v-model.lazy="item.end"
              type="time"
              :aria-label="`Окончание перерыва ${index + 1}`"
            />
          </label>
          <button
            class="w-button"
            type="button"
            :aria-label="`Убрать перерыв ${index + 1}`"
            @click="store.removePlanBreak(index)"
          >
            <q-icon name="close" />
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="store.planPreview"
      class="w-preview"
      :class="store.planPreview.ok ? 'w-notice' : 'w-error'"
      :role="store.planPreview.ok ? 'status' : 'alert'"
    >
      {{ store.planPreview.text }}
    </div>

    <div class="w-plan-bottom">
      <div class="w-plan-summary">{{ summary }}</div>
      <div class="w-actions">
        <button class="w-button" type="button" @click="store.previewPlan()">Проверить</button>
        <button class="w-button primary" type="button" @click="store.applyPlan()">
          Сохранить график
        </button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useScheduleStore } from '@/store/calendar/scheduleModule';
import { MAX_PATTERN_DAYS } from '~utils/calendarModel';
import type { PlanPattern } from '@/store/calendar/types';

const PATTERNS: { value: PlanPattern; label: string }[] = [
  { value: 'once', label: 'Выбранный день' },
  { value: 'weekday', label: 'По будням' },
  { value: 'every', label: 'Каждый день' },
  { value: '2on2', label: '2 рабочих / 2 выходных' },
];

const store = useScheduleStore();
const plan = computed(() => store.plan);

const summary = computed(() => {
  const days = store.patternSpec.dates.length;
  if (!days) return `Укажите корректный период до ${MAX_PATTERN_DAYS} дней.`;
  return `${days} дн. · приём ${plan.value.duration} мин. · `
    + `регламентированных перерывов в день: ${plan.value.breaks.length}`;
});

// Результат проверки относится к конкретному плану — после правки он устаревает
watch(plan, () => {
  store.planPreview = null;
}, { deep: true });
</script>
